import { getAuthHeaderAndShopperId } from '@vp/common-api-utils/authHelpers';
import {
  trackLegacyDocumentConversionAttempted,
  trackLegacyDocumentConversionAttemptFailed,
  trackLegacyDocumentConversionSuccess,
  trackReorderAttempted,
  trackReorderAttemptFailed,
  trackReorderSuccess,
  trackBuyNowAttempted,
  trackBuyNowSuccess,
  trackBuyNowAttemptFailed,
} from './newrelic/newRelicTrackingApi';
import {
  createAuthorizationHeaders,
  createContentTypeHeaders,
  observableFetchClient,
} from '@vp/common-api-utils';
import type { SourcePage } from '../types/sourcePage.types';
import {
  convertLegacyDocumentAttempted,
  orderReorderV2Attempted,
  expressCheckoutBuyNowAttempted,
} from './trackingApi';

const prodReorderBaseUrl = 'https://oreos.orders.vpsvc.com';
const stagingReorderBaseUrl = 'https://oreos-staging.orders.vpsvc.com';

/**
 * Returns an object containing parameters to use for calling the reorder service.
 * In the future, consider adding timeouts as part of the configuration.
 * @param {*} environment The environment to send reorder service requests too.
 */
function getConfigForEnvironment(environment: string) {
  if (!environment) {
    console.error(
      `Unable to derive service config for a null/empty environment, using PRODUCTION environment as sensible fallback.`
    );
    return { baseUrl: prodReorderBaseUrl };
  }
  switch (environment) {
    case 'PRODUCTION':
      return { baseUrl: prodReorderBaseUrl };
    case 'STAGING':
      return { baseUrl: stagingReorderBaseUrl };
    default:
      console.warn(
        `Unsupported environment type of ${environment}, using PRODUCTION environment as sensible fallback.`
      );
      return { baseUrl: prodReorderBaseUrl };
  }
}

/**
 * Checks to see if at least one result in the provided results array, was successful
 * @param {{successful: boolean}[]} results results from request
 * @returns {boolean} whether or not at least one of the results was successful
 */
const hasAtLeastOneSuccessfulResult = (results: { successful: boolean }[]) =>
  results && results.some((result) => result.successful === true);

/**
 * When requesting to reorder multiple items, there can sometimes be partial failure/success.
 * This method reads the response and determines if the entire request failed or not.
 * If it was not a success, it will throw an exception.
 * @param {Response} response
 */
const ensureReorderRequestWasSuccessfulOrThrow = async (response: {
  status: number;
  json: () => Promise<{
    altCartId?: string;
    results: { successful: boolean }[];
  }>;
}) => {
  if (response && response.status === 200) {
    const data = await response.json();
    const { results } = data;
    if (hasAtLeastOneSuccessfulResult(results)) {
      return data;
    }
    // throw exception with the failed results
    const errorObj = {
      results,
    };
    throw errorObj;
  }
  throw new Error('request to reorder failed');
};

interface ReorderItemsWithSpecifiedQuantitiesProps {
  orderNumber: string;
  reorderItems: Array<{ lineItemId: string; quantity: number }>;
  reorderSourcePage: SourcePage;
  shopperId: string;
  tenantEnvironment: string;
  vistacartTenantName: string;
}

/**
 * Invoke the reorderService to reorder this set of lineItem(s)
 * @param {*} props - the props for the reorder request
 * @param {string} props.orderNumber - order that is being reordered
 * @param {string} props.shopperId - ID of the shopper making the request
 * @param {string} props.reorderSourcePage - which page did the reorder request come from (OH v BIA)
 * @param {*} props.reorderItems - list of lineItem IDs that are part of the reorder
 * @param {string} props.tenantEnvironment - production or staging?
 * @param {string} props.vistacartTenantName - which tenant cart the items should be going into
 */
export const reorderItemsWithSpecifiedQuantities = async (
  props: ReorderItemsWithSpecifiedQuantitiesProps
) => {
  const {
    orderNumber,
    reorderItems,
    reorderSourcePage,
    shopperId,
    tenantEnvironment,
    vistacartTenantName,
  } = props;
  const { baseUrl } = getConfigForEnvironment(tenantEnvironment);
  const metricsAdditionalData = {
    orderId: orderNumber,
    cartTenant: vistacartTenantName,
    page: reorderSourcePage,
    lineItemIds: reorderItems.map((item) => item.lineItemId),
  };
  const url = `${baseUrl}/v2/reorder/shoppers/${shopperId}/orders/${orderNumber}/cartTenant/${vistacartTenantName}`;

  // notify segment for analytics
  orderReorderV2Attempted(orderNumber, reorderItems);
  // notify NR for tracking/monitoring
  trackReorderAttempted(metricsAdditionalData);

  const { authHeader } = getAuthHeaderAndShopperId();

  try {
    const response = await observableFetchClient({
      url,
      method: 'post',
      headers: new Headers({
        ...createContentTypeHeaders(),
        ...createAuthorizationHeaders(authHeader),
      }),
      body: JSON.stringify(reorderItems),
    });
    await ensureReorderRequestWasSuccessfulOrThrow(response);
    trackReorderSuccess(metricsAdditionalData);
  } catch (err) {
    if (err instanceof Error) {
      trackReorderAttemptFailed(err, metricsAdditionalData);
    }
    throw err;
  }
};

interface ConvertLegacyDocumentProps {
  lineItemId: string;
  orderNumber: string;
  shopperId: string;
  sourcePage: string;
  tenant: string;
  tenantEnvironment: string;
}

/**
 * Invoke OREOs to convert the legacy document for a line item
 * @param {string} environment - production or staging?
 * @param {string} orderNumber - order that is being reordered
 * @param {string} lineItemId - lineItem ID that are part of the reorder
 * @param {string} tenant - which tenant cart the items should be going into
 * @param {string} sourcePage - which page did the reorder request come from (OD vs OH)
 */
export const convertLegacyDocument = async (
  props: ConvertLegacyDocumentProps
) => {
  const {
    lineItemId,
    orderNumber,
    shopperId,
    sourcePage,
    tenant,
    tenantEnvironment,
  } = props;
  const { baseUrl } = getConfigForEnvironment(tenantEnvironment);
  const metricsAdditionalData = {
    orderId: orderNumber,
    itemId: lineItemId,
    cartTenant: tenant,
    page: sourcePage,
  };
  const url = `${baseUrl}/v2/shoppers/${shopperId}/orders/${orderNumber}/items/${lineItemId}/convertLegacyDocument`;
  const DOCUMENT_CONVERSION_FAULURE_ERROR_MESSAGE =
    'request to convert a legacy document failed';
  // notify segment for analytics
  convertLegacyDocumentAttempted({ orderNumber, lineItemId });

  // notify NR for tracking/monitoring
  trackLegacyDocumentConversionAttempted(metricsAdditionalData);

  const { authHeader } = getAuthHeaderAndShopperId();

  try {
    const response = await observableFetchClient({
      url,
      method: 'post',
      headers: new Headers({
        ...createContentTypeHeaders(),
        ...createAuthorizationHeaders(authHeader),
      }),
    });
    const data = await response.json();
    if (data.successful) {
      trackLegacyDocumentConversionSuccess(metricsAdditionalData);
    } else {
      const conversionError = new Error(
        DOCUMENT_CONVERSION_FAULURE_ERROR_MESSAGE
      );
      trackLegacyDocumentConversionAttemptFailed(
        conversionError,
        metricsAdditionalData
      );
    }
    return data;
  } catch (err) {
    const conversionError = new Error(
      DOCUMENT_CONVERSION_FAULURE_ERROR_MESSAGE,
      { cause: err }
    );
    trackLegacyDocumentConversionAttemptFailed(
      conversionError,
      metricsAdditionalData
    );
    throw conversionError;
  }
};

interface ReorderForAltCheckoutProps {
  tenantEnvironment: string;
  orderNumber: string;
  reorderItems: Array<{
    lineItemId: string;
    quantity: number;
    analytics: { source: string };
    displayPosition: number;
    productId: string;
  }>;
  vistacartTenantName: string;
  reorderSourcePage: SourcePage;
  reorderSourcePageName: string;
  shopperId: string;
}

/**
 * Invoke the reorderService to buy this item now
 * @param {*} environment - production or staging?
 * @param {*} orderNumber - order that is being reordered
 * @param {*} reorderItems - list of lineItem IDs that are part of the reorder
 * @param {*} tenantId - which tenant cart the items should be going into
 * @param {*} reorderSourcePage - which tab did the reorder request come from
 * @param {*} reorderSourcePageName - the name of the page the reorder request come from
 * @param {*} shopperId - the shopper ID
 */
export const reorderForAltCheckout = async ({
  tenantEnvironment,
  orderNumber,
  reorderItems,
  vistacartTenantName,
  reorderSourcePage,
  reorderSourcePageName,
  shopperId,
}: ReorderForAltCheckoutProps) => {
  const { baseUrl } = getConfigForEnvironment(tenantEnvironment);
  const { authHeader } = getAuthHeaderAndShopperId();
  const metricsAdditionalData = {
    orderNumber,
    cartTenant: vistacartTenantName,
    page: reorderSourcePage,
    lineItemIds: reorderItems.map((item) => item.lineItemId),
  };
  // construct the request body from "reorderItems"
  const body = reorderItems.map(({ lineItemId, quantity, analytics }) => ({
    lineItemId,
    quantity,
    analytics,
  }));
  const url = `${baseUrl}/reorderForAltCheckout/shoppers/${shopperId}/orders/${orderNumber}/cartTenant/${vistacartTenantName}`;

  // notify segment for analytics
  expressCheckoutBuyNowAttempted(
    orderNumber,
    reorderItems,
    reorderSourcePageName
  );

  // notify NR for tracking/monitoring
  trackBuyNowAttempted(metricsAdditionalData);

  try {
    const response = await observableFetchClient({
      url,
      method: 'post',
      headers: new Headers({
        ...createContentTypeHeaders(),
        ...createAuthorizationHeaders(authHeader),
      }),
      body: JSON.stringify(body),
    });
    const data = await ensureReorderRequestWasSuccessfulOrThrow(response);
    trackBuyNowSuccess(metricsAdditionalData);
    return data;
  } catch (err) {
    if (err instanceof Error) {
      trackBuyNowAttemptFailed(err, metricsAdditionalData);
    }
    throw err;
  }
};
